import { type FC, useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { DialogHeader } from "@/shared/components";
import {
  useCancelEventMutation,
  useEventCancelPreviewQuery,
} from "@/partner/entities/products";
import { PAYMENT_PROVIDER_LABEL_KEY } from "@/partner/shared/utils/paymentProvider";
import { formatAmountObject } from "@/partner/shared/utils/formatCurrency";
import {
  CANCEL_EVENT_FORM_ERROR_KEYS,
  EVENT_CANCEL_MESSAGE_MAX,
  validateCancelEventForm,
} from "../model";

interface CancelEventDialogProps {
  productId: string;
  open: boolean;
  onClose(): void;
  onStarted(): void;
}

/**
 * Окно отмены PPV-события (спека viewer-money, часть B): сколько покупок и
 * денег вернётся, разбивка по провайдерам, ручные возвраты списком,
 * бесплатные числом, сообщение зрителям и подтверждение вводом названия.
 * Идущий эфир отмену запрещает. Короткий диалог по PARTNER-PAGES 3.1.
 */
export const CancelEventDialog: FC<CancelEventDialogProps> = ({
  productId,
  open,
  onClose,
  onStarted,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [confirmTitle, setConfirmTitle] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (open) {
      setConfirmTitle("");
      setMessage("");
    }
  }, [open]);

  const { data, loading } = useEventCancelPreviewQuery({
    variables: { productId },
    skip: !open,
    fetchPolicy: "network-only",
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
      onClose();
    },
  });

  const [cancelEvent, { loading: cancelling, error, reset }] =
    useCancelEventMutation({
      refetchQueries: ["Product", "EventCancelProgress", "ProductsList"],
      onCompleted: () => {
        enqueueSnackbar(t("products.eventCancel.success"), {
          variant: "success",
        });
        onStarted();
      },
    });

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const preview = data?.eventCancelPreview;
  const errors = validateCancelEventForm({
    confirmTitle,
    productTitle: preview?.productTitle ?? "",
    message,
  });
  const liveBlocked = Boolean(preview && preview.liveStreams > 0);
  const canSubmit =
    Boolean(preview) &&
    !liveBlocked &&
    !cancelling &&
    Object.keys(errors).length === 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    void cancelEvent({
      variables: {
        input: {
          productId,
          confirmTitle: confirmTitle.trim(),
          message: message.trim() || null,
        },
      },
    }).catch(() => {
      // ошибка показывается Alert'ом из error мутации
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="body"
      fullScreen={fullScreen}
      onClick={(event) => event.stopPropagation()}
    >
      <DialogHeader
        title={t("products.eventCancel.dialogTitle")}
        loading={loading || cancelling}
        onClose={onClose}
      />
      <DialogContent sx={{ px: 2, pb: 2 }}>
        {preview && (
          <Stack spacing={2}>
            <Typography variant="subtitle1">{preview.productTitle}</Typography>
            <Stack spacing={0.5}>
              <Typography variant="h6">
                {preview.amount
                  ? t("products.eventCancel.amount", {
                      amount: formatAmountObject(preview.amount),
                    })
                  : t("products.eventCancel.amountUnknown")}
              </Typography>
              <Typography color="text.secondary">
                {t("products.eventCancel.purchases", {
                  count: preview.purchases,
                })}
              </Typography>
            </Stack>

            {preview.byProvider.length > 0 && (
              <List
                dense
                disablePadding
                subheader={
                  <ListSubheader disableGutters>
                    {t("products.eventCancel.byProviderTitle")}
                  </ListSubheader>
                }
              >
                {preview.byProvider.map((row) => (
                  <ListItem key={row.provider} disableGutters>
                    <ListItemText
                      primary={t(PAYMENT_PROVIDER_LABEL_KEY[row.provider])}
                      secondary={t("products.eventCancel.purchases", {
                        count: row.count,
                      })}
                    />
                    <Typography>{formatAmountObject(row.amount)}</Typography>
                  </ListItem>
                ))}
              </List>
            )}

            {preview.manualItems.length > 0 && (
              <Alert severity="warning">
                <AlertTitle>{t("products.eventCancel.manualTitle")}</AlertTitle>
                {t("products.eventCancel.manualText")}
                <List dense disablePadding>
                  {preview.manualItems.map((item) => (
                    <ListItem key={item.purchaseId} disableGutters>
                      <ListItemText
                        primary={`${item.buyerEmail ?? item.purchaseId}, ${formatAmountObject(item.amount)}`}
                        secondary={[
                          item.provider
                            ? t(PAYMENT_PROVIDER_LABEL_KEY[item.provider])
                            : null,
                          item.providerPaymentId,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            {preview.free > 0 && (
              <Typography color="text.secondary">
                {t("products.eventCancel.free", { count: preview.free })}
              </Typography>
            )}
            {preview.pendingStreams > 0 && (
              <Typography color="text.secondary">
                {t("products.eventCancel.pendingStreams", {
                  count: preview.pendingStreams,
                })}
              </Typography>
            )}
            {preview.sharedStreams > 0 && (
              <Alert severity="info">
                {t("products.eventCancel.sharedStreams", {
                  count: preview.sharedStreams,
                })}
              </Alert>
            )}
            {preview.unknownPayments > 0 && (
              <Alert severity="warning">
                {t("products.eventCancel.unknownPayments", {
                  count: preview.unknownPayments,
                })}
              </Alert>
            )}
            <Alert severity="info">{t("products.eventCancel.feesNote")}</Alert>
            {liveBlocked && (
              <Alert severity="error">
                {t("products.eventCancel.liveBlocked")}
              </Alert>
            )}

            <TextField
              label={t("products.eventCancel.messageLabel")}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              multiline
              minRows={3}
              fullWidth
              error={Boolean(errors.message)}
              helperText={
                errors.message
                  ? t(CANCEL_EVENT_FORM_ERROR_KEYS[errors.message])
                  : t("products.eventCancel.messageHelper")
              }
              slotProps={{
                htmlInput: { maxLength: EVENT_CANCEL_MESSAGE_MAX + 100 },
              }}
            />
            <TextField
              label={t("products.eventCancel.confirmLabel")}
              placeholder={preview.productTitle}
              value={confirmTitle}
              onChange={(event) => setConfirmTitle(event.target.value)}
              fullWidth
              error={Boolean(confirmTitle && errors.confirmTitle)}
              helperText={
                confirmTitle && errors.confirmTitle
                  ? t(CANCEL_EVENT_FORM_ERROR_KEYS[errors.confirmTitle])
                  : undefined
              }
            />

            {error && <Alert severity="error">{error.message}</Alert>}
            <Typography variant="body2" color="warning.main">
              {t("products.eventCancel.irreversible")}
            </Typography>
            <Button
              color="error"
              variant="contained"
              disableElevation
              fullWidth
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {t("products.eventCancel.submit")}
            </Button>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};
